import type { FontSizeRange, TextAlignment, FontFamily } from './types'

/**
 * Минимальный размер шрифта маркировки (px)
 */
export const MIN_FONT_SIZE: FontSizeRange = 4

/**
 * Максимальный размер шрифта маркировки (px)
 */
export const MAX_FONT_SIZE: FontSizeRange = 18

/**
 * Размер шрифта по умолчанию (px)
 */
export const DEFAULT_FONT_SIZE: FontSizeRange = 12

/**
 * Выравнивание по умолчанию
 */
export const DEFAULT_ALIGNMENT: TextAlignment = 'center'

/**
 * Шрифт по умолчанию
 */
export const DEFAULT_FONT_FAMILY: FontFamily = 'Inter'

/**
 * Охранные поля для размещения медиа во фрейме
 */
export const SAFE_AREA = {
    top: 20, // px
    bottom: 20, // px
    left: 30, // px
    right: 30, // px
} as const

/**
 * Доступные шрифты для маркировки
 */
export const AVAILABLE_FONTS: readonly FontFamily[] = [
    'Inter',
    'Onest',
    'Times',
    'Montserrat',
] as const

/**
 * Доступные варианты выравнивания
 */
export const AVAILABLE_ALIGNMENTS: readonly TextAlignment[] = [
    'left',
    'center',
    'right',
] as const

/**
 * Цвета по умолчанию (для миграции старых данных)
 */
export const DEFAULT_COLORS = {
    light: '#FFFFFF', // Светлая подпись
    dark: '#000000', // Темная подпись
} as const

/**
 * Предустановленные цвета для выбора в палитре
 * Формат: hex цвет с alpha каналом (например, "#056FFF80")
 * Порядок: 10 цветов в указанном порядке
 */
export const PRESET_COLORS: readonly string[] = [
    '#FFFFFFFF', // Белый (с черным бордером)
    '#DAE1F1FF', // Светло-серый
    '#667588FF', // Серый
    '#232323FF', // Темно-серый
    '#ED5651FF', // Красный
    '#FF7D57FF', // Оранжевый
    '#F5FF6FFF', // Желтый
    '#46FF91FF', // Зеленый
    '#46ACFFFF', // Синий
    '#954AFFFF', // Фиолетовый
] as const

/**
 * Константы для ColorPickerPopup
 * Референс: 1440x940
 */
export const COLOR_PICKER = {
    /**
     * Ширина попапа (px) - адаптивно от 120px на 1440x940
     */
    POPUP_WIDTH: 120,
    /**
     * Высота попапа (px) - адаптивно от 112px на 1440x940
     */
    POPUP_HEIGHT: 112,
    /**
     * Размер предустановленного цвета (px)
     */
    PRESET_COLOR_SIZE: 16,
    /**
     * Отступ между предустановленными цветами (px)
     */
    PRESET_COLOR_GAP: 4,
    /**
     * Количество цветов в ряду
     */
    PRESET_COLORS_PER_ROW: 5,
    /**
     * Высота полоски прозрачности (px)
     */
    ALPHA_SLIDER_HEIGHT: 8,
    /**
     * Высота инпута hex (px)
     */
    HEX_INPUT_HEIGHT: 24,
} as const

/**
 * URL заглушки для медиа (временно, пока не приходит с бэкенда)
 */
export const PLACEHOLDER_IMAGE_URL =
    'https://via.placeholder.com/800x600/cccccc/666666?text=Media+Placeholder'

/**
 * Размеры кастомного контрола масштабирования
 */
export const RESIZE_CONTROL = {
    /**
     * Диаметр круглой кнопки контрола (px)
     */
    BUTTON_SIZE: 12,
    /**
     * Толщина обводки кнопки (px)
     */
    STROKE_WIDTH: 2,
    /**
     * Толщина линий иконки стрелок (px)
     */
    ICON_STROKE_WIDTH: 1.5,
    /**
     * Цвет заливки кнопки
     */
    FILL_COLOR: '#4795FF',
    /**
     * Цвет обводки кнопки
     */
    STROKE_COLOR: '#FFFFFF',
} as const

/**
 * Настройки обводки текстового объекта на canvas
 */
export const TEXT_BORDER_STYLES = {
    /**
     * Цвет обводки при выделении
     */
    COLOR: '#4795FF',
    /**
     * Пунктирная линия (массив [длина линии, длина пробела])
     */
    DASH_ARRAY: [5, 5] as const,
    /**
     * Масштаб обводки
     */
    SCALE_FACTOR: 1,
} as const

/**
 * Константы для TextEditor
 */
export const TEXT_EDITOR = {
    /**
     * Минимальный размер шрифта (px)
     */
    MIN_FONT_SIZE: 6,
    /**
     * Максимальный размер шрифта (px)
     */
    MAX_FONT_SIZE: 16,
    /**
     * Шаг изменения размера шрифта при адаптации (px)
     */
    FONT_SIZE_STEP: 0.5,
    /**
     * Line height множитель
     */
    LINE_HEIGHT: 1.3,
    /**
     * Padding: top/bottom (px)
     */
    PADDING_VERTICAL: 2,
    /**
     * Padding: left/right (px)
     */
    PADDING_HORIZONTAL: 6,
} as const

/**
 * Позиция контрола масштабирования относительно текстового объекта
 */
export const RESIZE_CONTROL_POSITION = {
    /**
     * Позиция X (0.5 = правый край объекта)
     */
    X: 0.5,
    /**
     * Позиция Y (-0.5 = верхний край объекта)
     */
    Y: -0.5,
} as const

/**
 * Константы для Canvas
 */
export const CANVAS = {
    /**
     * Цвет фона canvas
     */
    BACKGROUND_COLOR: '#f5f5f5',
    /**
     * Цвет текста по умолчанию
     */
    DEFAULT_TEXT_COLOR: '#000000',
    /**
     * Таймаут debounce для обработчика resize (мс)
     */
    RESIZE_DEBOUNCE_TIMEOUT: 100,
    /**
     * Ширина canvas в модальном окне редактора (px)
     */
    MODAL_WIDTH: 190,
    /**
     * Высота canvas в модальном окне редактора (px)
     */
    MODAL_HEIGHT: 330,
} as const

/**
 * Позиция текста по умолчанию (в процентах от размеров canvas)
 * Используется при создании нового текстового объекта, если позиция не задана
 */
export const DEFAULT_TEXT_POSITION = {
    /**
     * Позиция X по умолчанию (50% = центр по горизонтали)
     */
    X: 50,
    /**
     * Позиция Y по умолчанию (90% = внизу по вертикали)
     */
    Y: 90,
} as const

/**
 * Отступы для текстового объекта
 */
export const TEXT_OBJECT_PADDING = {
    /**
     * Отступ пунктирной линии от текста (px)
     */
    BORDER: 10,
    /**
     * Отступ подложки за текстом (px)
     * Соответствует Фигме: расстояние от шрифта до края подложки
     */
    BACKGROUND: 2,
    /**
     * Минимальная ширина текста с учетом отступов (px)
     */
    MIN_WIDTH: 10,
} as const

/**
 * Скругление углов подложки текста (px)
 * Соответствует Фигме
 */
export const TEXT_OBJECT_BACKGROUND_BORDER_RADIUS = 2 as const

/**
 * Межстрочное расстояние для текстового объекта
 */
export const TEXT_OBJECT_LINE_HEIGHT = 1 as const

/**
 * Базовые настройки текстового объекта Fabric.js
 */
export const TEXT_OBJECT_DEFAULT_OPTIONS = {
    /**
     * Точка привязки по X (center = центр объекта)
     */
    originX: 'center' as const,
    /**
     * Точка привязки по Y (center = центр объекта)
     */
    originY: 'center' as const,
    /**
     * Объект можно выделять и перемещать
     */
    selectable: true,
    /**
     * Включены контролы для изменения размера
     */
    hasControls: true,
    /**
     * Включена обводка при выделении
     */
    hasBorders: true,
    /**
     * Блокировать поворот
     */
    lockRotation: true,
    /**
     * Блокировать искажение по X
     */
    lockSkewingX: true,
    /**
     * Блокировать искажение по Y
     */
    lockSkewingY: true,
    /**
     * Блокировать зеркальное отражение при масштабировании
     */
    lockScalingFlip: true,
} as const
